import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KeyRound, ShieldAlert, Loader2, CheckCircle2, Copy, Check } from "lucide-react";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import { useToast } from "@/hooks/use-toast";

interface PasskeyPromptProps {
  open: boolean;
  onComplete: () => void;
}

const PasskeyPrompt = ({ open, onComplete }: PasskeyPromptProps) => {
  const { register, loading } = useWebAuthn();
  const { toast } = useToast();
  const [success, setSuccess] = useState(false);
  const [credentialId, setCredentialId] = useState("");
  const [copied, setCopied] = useState(false);

  const handleRegister = async () => {
    const result = await register();
    console.log("Passkey register result:", JSON.stringify(result));
    if (result.success) {
      const cid = result.credentialId || "";
      console.log("Credential ID received:", cid);
      setCredentialId(cid);
      setSuccess(true);
      toast({ title: "تم إنشاء مفتاح Passkey بنجاح ✅" });
    } else {
      toast({
        title: "فشل إنشاء مفتاح Passkey",
        description: result.error || "حدث خطأ، حاول مرة أخرى",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
              {success ? (
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              ) : (
                <KeyRound className="w-8 h-8 text-primary" />
              )}
            </div>
          </div>
          <DialogTitle className="text-xl">
            {success ? "تم إنشاء المفتاح بنجاح!" : "إنشاء مفتاح Passkey"}
          </DialogTitle>
          <DialogDescription className="text-center space-y-3 pt-2" asChild>
            <div>
              {success ? (
                <div className="space-y-4">
                  <p className="text-green-600 font-medium text-base">✅ تم حفظ مفتاح Passkey في متصفحك بنجاح</p>

                  <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-right">
                    <ShieldAlert className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-700 dark:text-amber-400 font-medium space-y-1">
                      <p>⚠️ تنبيه مهم:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>سيتم حفظ المفتاح في مدير كلمات المرور في متصفحك</li>
                        <li>لا تقم بحذف المفتاح من إعدادات المتصفح أو الجهاز</li>
                        <li>يُنصح بتفعيل مزامنة كلمات المرور في حساب Google أو iCloud</li>
                      </ul>
                    </div>
                  </div>

                  <Button onClick={onComplete} className="w-full h-11 gradient-primary text-primary-foreground font-semibold">
                    متابعة
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    سيتم إنشاء مفتاح Passkey خاص بحسابك وحفظه تلقائياً في متصفحك (مثل Chrome أو Safari). يمكنك استخدامه لتسجيل الدخول بسرعة وأمان.
                  </p>

                  <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-right">
                    <ShieldAlert className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-700 dark:text-amber-400 font-medium space-y-1">
                      <p>⚠️ تنبيه مهم:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>سيتم حفظ المفتاح في مدير كلمات المرور في متصفحك</li>
                        <li>لا تقم بحذف المفتاح من إعدادات المتصفح أو الجهاز</li>
                        <li>يُنصح بتفعيل مزامنة كلمات المرور في حساب Google أو iCloud</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4 text-right">
                    <KeyRound className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div className="text-sm text-foreground space-y-1">
                      <p className="font-medium">📌 كيف تحفظ المفتاح؟</p>
                      <ul className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
                        <li>اضغط على "إنشاء مفتاح Passkey" أدناه</li>
                        <li>سيطلب منك المتصفح تأكيد الحفظ — اضغط "حفظ" أو "Save"</li>
                        <li>سيُحفظ المفتاح تلقائياً في Chrome/Safari</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {!success ? (
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={handleRegister}
              disabled={loading}
              className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-base gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l2 2"/></svg>
                  حفظ المفتاح في Chrome
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onComplete}
              className="w-full h-10 text-sm"
            >
              يوجد عندي بالفعل
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              📝 قم بتدوين أو حفظ المفتاح في مكان آمن — لن يُعرض مرة أخرى
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 mt-4">
            <div className="bg-muted/50 border border-border rounded-xl p-4 text-right space-y-2">
              <p className="text-sm font-medium text-foreground">🔑 معرّف المفتاح (احفظه في مكان آمن):</p>
              <div className="flex items-center gap-2 bg-background rounded-lg p-3 border border-border">
                <code className="text-xs text-foreground break-all flex-1 text-left font-mono" dir="ltr">
                  {credentialId || "غير متاح — المفتاح محفوظ في المتصفح فقط"}
                </code>
                {credentialId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-8 w-8 p-0"
                    onClick={() => {
                      navigator.clipboard.writeText(credentialId);
                      setCopied(true);
                      toast({ title: "تم النسخ ✅" });
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">⚠️ احفظ هذا المعرّف — لن يُعرض مرة أخرى</p>
            </div>

            <Button onClick={onComplete} className="w-full h-11 gradient-primary text-primary-foreground font-semibold">
              متابعة
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PasskeyPrompt;
